import {
    mdiAccountCashOutline,
    mdiAccountTie,
    mdiChevronDown,
    mdiChevronRight,
    mdiClose,
    mdiCodeBraces,
    mdiGavel,
    mdiLockOutline,
    mdiMagnify,
    mdiMinusCircleOutline,
    mdiSortVariant,
    mdiTagOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Variables } from '../../base';
import {
    AutomationWorker,
    RuleActionType,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import { getActionMeta } from '../../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import * as SidebarS from '../sidebar/configuration-sidebar.styles';
import { ConfigurationRuleCard } from './configuration-rule-card';
import * as S from './configuration-rule.styles';
import {
    buildRuleTableRows,
    groupRuleTableRows,
    matchesRuleQuery,
    RULE_ACTION_ORDER,
    RulesActionFilter,
    RulesGroupBy,
} from './configuration-rules-table.utils';

interface IConfigurationRulesTableProps {
    disabled: boolean;
    store: WorkerStore;
    worker: AutomationWorker;
}

const ACTION_ICONS: Record<RuleActionType, string> = {
    CLOSE_OUT: mdiMinusCircleOutline,
    SWITCH_TO_PRIVATE_PAY: mdiAccountCashOutline,
    CREATE_APPEAL: mdiGavel,
    CREATE_MESSAGE: mdiAccountTie,
    EXECUTE_ENTER_FUNCTION: mdiCodeBraces,
};

function formatRuleCount(count: number): string {
    return `${count} rule${count === 1 ? '' : 's'}`;
}

/**
 * Rules as a table: search, action filter and group-by over the worker's rules; each row expands
 * into the rule editor. Rows always show their real priority — evaluation order is top-down over
 * the full list, so drag-reorder is only offered on the unfiltered, ungrouped view.
 */
export const ConfigurationRulesTable: React.FC<IConfigurationRulesTableProps> = observer(
    ({ disabled, store, worker }) => {
        const [query, setQuery] = useState('');
        const [actionFilter, setActionFilter] = useState<RulesActionFilter>('ALL');
        const [requestedGroupBy, setGroupBy] = useState<RulesGroupBy>('PRIORITY');
        const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

        const showCodeColumn = store.getAvailableMatchScopesForWorker().includes('PAYMENT');
        const groupBy: RulesGroupBy =
            requestedGroupBy === 'CARC' && !showCodeColumn ? 'PRIORITY' : requestedGroupBy;
        const columns = S.getRulesTableColumns(showCodeColumn);

        const rows = buildRuleTableRows(
            worker.rules,
            showCodeColumn,
            store.catalog,
            store.payerById,
            store.providerById,
            store.facilityById,
        );
        const textHits = rows.filter(row => matchesRuleQuery(row, query));
        const visible =
            actionFilter === 'ALL' ? textHits : textHits.filter(row => row.actionType === actionFilter);
        const groups = groupRuleTableRows(visible, groupBy);
        const canReorder = !query.trim() && actionFilter === 'ALL' && groupBy === 'PRIORITY';

        const actionFilters = RULE_ACTION_ORDER.map(type => ({
            type,
            count: textHits.filter(row => row.actionType === type).length,
        })).filter(item => item.count > 0 || item.type === actionFilter);

        const groupOptions: Array<{ label: string; value: RulesGroupBy }> = [
            { value: 'PRIORITY', label: 'Priority' },
            { value: 'ACTION', label: 'Action' },
            ...(showCodeColumn ? [{ value: 'CARC' as const, label: 'CARC group' }] : []),
        ];

        const clearFilters = (): void => {
            setQuery('');
            setActionFilter('ALL');
        };

        return (
            <>
                <S.RulesToolbar>
                    <S.RulesToolbarRow>
                        <S.RulesSearchWrap>
                            <SidebarS.SearchField>
                                <Icon path={mdiMagnify} size={0.7} color={Variables.Colors.n50} aria-hidden={true} />
                                <SidebarS.SearchInput
                                    value={query}
                                    onChange={event => setQuery(event.target.value)}
                                    placeholder={
                                        showCodeColumn
                                            ? 'Search by rule name, CARC / RARC code, action'
                                            : 'Search by rule name, condition, action'
                                    }
                                    aria-label="Search rules"
                                />
                                {query && (
                                    <S.RulesSearchClear
                                        type="button"
                                        aria-label="Clear search"
                                        onClick={() => setQuery('')}>
                                        <Icon path={mdiClose} size={0.65} aria-hidden={true} />
                                    </S.RulesSearchClear>
                                )}
                            </SidebarS.SearchField>
                        </S.RulesSearchWrap>

                        <S.RulesToolbarLabel>Group by</S.RulesToolbarLabel>
                        <S.RulesGroupBySwitch role="group" aria-label="Group rules by">
                            {groupOptions.map(option => (
                                <S.RulesGroupByOption
                                    key={option.value}
                                    type="button"
                                    active={groupBy === option.value}
                                    aria-pressed={groupBy === option.value}
                                    onClick={() => setGroupBy(option.value)}>
                                    {option.label}
                                </S.RulesGroupByOption>
                            ))}
                        </S.RulesGroupBySwitch>

                        <S.RulesToolbarSpacer />
                        <S.RulesCount>{formatRuleCount(worker.rules.length)}</S.RulesCount>
                    </S.RulesToolbarRow>

                    <S.RulesToolbarRow>
                        <S.RulesActionFilter
                            type="button"
                            active={actionFilter === 'ALL'}
                            aria-pressed={actionFilter === 'ALL'}
                            onClick={() => setActionFilter('ALL')}>
                            All actions
                            <S.RulesActionFilterCount>{textHits.length}</S.RulesActionFilterCount>
                        </S.RulesActionFilter>
                        {actionFilters.map(({ type, count }) => {
                            const active = actionFilter === type;
                            return (
                                <S.RulesActionFilter
                                    key={type}
                                    type="button"
                                    active={active}
                                    aria-pressed={active}
                                    onClick={() => setActionFilter(active ? 'ALL' : type)}>
                                    <Icon path={ACTION_ICONS[type]} size={0.6} aria-hidden={true} />
                                    {getActionMeta(type).label}
                                    <S.RulesActionFilterCount>{count}</S.RulesActionFilterCount>
                                </S.RulesActionFilter>
                            );
                        })}

                        <S.RulesToolbarSpacer />
                        <S.RulesOrderHint>
                            <Icon
                                path={canReorder ? mdiSortVariant : mdiLockOutline}
                                size={0.65}
                                aria-hidden={true}
                            />
                            {canReorder
                                ? 'Top-down by priority — first match wins'
                                : 'Reorder disabled while filtered or grouped'}
                        </S.RulesOrderHint>
                    </S.RulesToolbarRow>
                </S.RulesToolbar>

                {visible.length === 0 && worker.rules.length > 0 && (
                    <S.RulesEmpty>
                        No rules match.
                        <S.RulesEmptyAction type="button" onClick={clearFilters}>
                            Clear filters
                        </S.RulesEmptyAction>
                    </S.RulesEmpty>
                )}

                {visible.length > 0 &&
                    groups.map(group => {
                        const groupKey = `${groupBy}:${group.key}`;
                        const open = !collapsedGroups[groupKey];

                        return (
                            <React.Fragment key={groupKey}>
                                {group.label !== null && (
                                    <S.RulesGroupHeader
                                        type="button"
                                        aria-expanded={open}
                                        onClick={() =>
                                            setCollapsedGroups(current => ({ ...current, [groupKey]: open }))
                                        }>
                                        <Icon
                                            path={open ? mdiChevronDown : mdiChevronRight}
                                            size={0.75}
                                            aria-hidden={true}
                                        />
                                        <Icon
                                            path={
                                                groupBy === 'ACTION'
                                                    ? (ACTION_ICONS[group.key as RuleActionType] ??
                                                      mdiTagOutline)
                                                    : mdiTagOutline
                                            }
                                            size={0.65}
                                            aria-hidden={true}
                                        />
                                        <S.RulesGroupTitle>{group.label}</S.RulesGroupTitle>
                                        <S.RulesGroupCount>{formatRuleCount(group.rows.length)}</S.RulesGroupCount>
                                        <S.RulesGroupLine />
                                    </S.RulesGroupHeader>
                                )}

                                {open && (
                                    <S.RulesTable>
                                        <S.RulesTableInner>
                                            <S.RulesTableHead columns={columns}>
                                                <span />
                                                <span>#</span>
                                                <span>Rule</span>
                                                {showCodeColumn && <span>CARC / RARC</span>}
                                                <span>{showCodeColumn ? 'Other conditions' : 'Conditions'}</span>
                                                <span>Action</span>
                                                <span />
                                            </S.RulesTableHead>
                                            {group.rows.map(row => (
                                                <ConfigurationRuleCard
                                                    key={row.rule.id}
                                                    row={row}
                                                    columns={columns}
                                                    showCodeColumn={showCodeColumn}
                                                    canReorder={canReorder}
                                                    worker={worker}
                                                    store={store}
                                                    disabled={disabled}
                                                />
                                            ))}
                                        </S.RulesTableInner>
                                    </S.RulesTable>
                                )}
                            </React.Fragment>
                        );
                    })}
            </>
        );
    },
);
